<template>
  <!-- ✅ EXACT SAME UI — NO CHANGES -->
  <div class="space-y-8">
    <!-- Header -->
    <div class="flex justify-between items-center flex-wrap gap-3 w-full">
      <h1 class="text-2xl font-semibold text-gray-800 flex items-center gap-2">
        <i class="ri-dashboard-line text-blue-600 text-2xl"></i>
        Dashboard
      </h1>

      <div class="flex gap-2">
        <button 
        @click="goToUpgrade()"
        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
          <i class="ri-price-tag-3-line mr-1"></i> Manage Plans
        </button>
      </div>
    </div>

    <!-- KPI Row -->
    <div v-if="dash_dt!=null" class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
      <div class="card text-center">
        <p class="text-xs text-gray-500 mb-1">Total Users</p>
        <h3 class="text-2xl font-bold text-gray-800">{{ dash_dt.total_users }}</h3>
      </div>
      <div class="card text-center">
        <p class="text-xs text-gray-500 mb-1">Paid Users</p>
        <h3 class="text-2xl font-bold text-green-600">{{ dash_dt.paid_users }}</h3>
      </div>
      <div class="card text-center">
        <p class="text-xs text-gray-500 mb-1">Free Users</p>
        <h3 class="text-2xl font-bold text-blue-600">{{ dash_dt.free_users }}</h3>
      </div>
      <div class="card text-center">
        <p class="text-xs text-gray-500 mb-1">Active Websites</p>
        <h3 class="text-2xl font-bold text-indigo-600">{{ dash_dt.act_websites }}</h3>
      </div>
      <div class="card text-center">
        <p class="text-xs text-gray-500 mb-1">Monthly Revenue</p>
        <h3 class="text-2xl font-bold text-gray-800">${{ dash_dt.montlhy_revenue }}</h3>
      </div>
      <div class="card text-center">
        <p class="text-xs text-gray-500 mb-1">Avg. Retention</p>
        <h3 class="text-2xl font-bold text-yellow-600">{{ dash_dt.avg_retention }}</h3>
      </div>
    </div>

    <!-- Row 2: Charts Section -->
    <div class="grid lg:grid-cols-3 gap-6">
      <div class="card col-span-2">
        <h3 class="text-lg font-semibold mb-2">Revenue Growth</h3>
        <p class="text-sm text-gray-500 mb-3">Month-by-month revenue performance</p>
        <div id="revenueChart" class="h-72 chart-wrapper"></div>
      </div>

      <div class="card">
        <h3 class="text-lg font-semibold mb-2">User Type Distribution</h3>
        <p class="text-sm text-gray-500 mb-3">Free vs Paid users</p>
        <div id="userTypeChart" class="h-72 chart-wrapper"></div>
      </div>
    </div>

    <div class="grid lg:grid-cols-2 gap-6">
      <div class="card col-span-2">
        <h3 class="text-lg font-semibold mb-3">Plan Performance</h3>
        <div id="planBarChart" class="h-72 chart-wrapper"></div>
      </div>
    </div>

    <div class="card">
      <h3 class="text-lg font-semibold mb-3">Recent Signups</h3>
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm text-gray-700">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-2 text-left">Name</th>
              <th class="px-4 py-2 text-left">Email</th>
              <th class="px-4 py-2 text-left">Plan</th>
              <th class="px-4 py-2 text-left">Type</th>
              <th class="px-4 py-2 text-center">Joined</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="user in latestUsers" :key="user.email" class="hover:bg-blue-50/40">
              <td class="px-4 py-2 font-medium">{{ user.name }}</td>
              <td class="px-4 py-2">{{ user.email }}</td>
              <td class="px-4 py-2">{{ user.plan }}</td>
              <td class="px-4 py-2">{{ user.type }}</td>
              <td class="px-4 py-2 text-center">{{ user.joined }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import ApexCharts from "apexcharts";
import { getDash } from "@/apis/webapis";

export default {
  name: "AdminDashboard",
  data() {
    return {
      ldata: null,
      dash_dt: null,
      latestUsers: [],
    };
  },
  async mounted() {
    if (sessionStorage.getItem("ldata")) {
      this.ldata = JSON.parse(sessionStorage.getItem("ldata"));
      await this.loadDashNow();
    }
  },
  methods: {
    async loadDashNow() {
      try {
        const res = await getDash(this.ldata.user_id);
        if (res.status) {
          this.dash_dt = res.dash_dt;

          // ✅ Map latest users (recent_users)
          this.latestUsers = this.dash_dt.recent_users.map(u => ({
            name: u.name || "—",
            email: u.email || "—",
            plan: u.user_plan_name || "Free",
            type: u.type || "user",
            joined: u.user_datetime
              ? new Date(u.user_datetime).toLocaleDateString()
              : "—",
          }));

          // ✅ Prepare chart data dynamically
          const revMonths = this.dash_dt.rev_grouwth.map(r => r.month);
          const revValues = this.dash_dt.rev_grouwth.map(r => parseFloat(r.total_revenue));

          const paid = this.dash_dt.paid_users || 0;
          const free = this.dash_dt.free_users || 0;

          const plans = this.dash_dt.plan_wise_users.map(p => p.plan_title);
          const planSubs = this.dash_dt.plan_wise_users.map(p => p.user_cnt);

          // ✅ Render charts with live data
          this.renderCharts(revMonths, revValues, [paid, free], plans, planSubs);
        }
      } catch (e) {
        console.error("Dashboard Load Error:", e);
      }
    },

    goToUpgrade() {
      this.$router.push("/pricing");
    },

    renderCharts(revMonths, revValues, userSplit, planNames, planCounts) {
      const fixChart = el => {
        const elem = document.querySelector(el);
        if (elem) {
          elem.style.width = "100%";
          elem.style.maxWidth = "100%";
          elem.style.overflow = "hidden";
          elem.style.display = "block";
        }
      };

      fixChart("#revenueChart");
      fixChart("#userTypeChart");
      fixChart("#planBarChart");

      // ✅ Revenue Growth Chart
      new ApexCharts(document.querySelector("#revenueChart"), {
        chart: { type: "area", height: 280, toolbar: { show: false } },
        series: [{ name: "Revenue", data: revValues }],
        colors: ["#2563eb"],
        fill: {
          type: "gradient",
          gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1 },
        },
        stroke: { curve: "smooth", width: 3 },
        xaxis: { categories: revMonths },
        grid: { borderColor: "#f3f4f6" },
      }).render();

      // ✅ User Split Chart
      new ApexCharts(document.querySelector("#userTypeChart"), {
        chart: { type: "donut", height: 280 },
        labels: ["Paid Users", "Free Users"],
        series: userSplit,
        colors: ["#16a34a", "#2563eb"],
        legend: { position: "bottom" },
        dataLabels: { enabled: false },
      }).render();

      // ✅ Plan Performance Chart
      new ApexCharts(document.querySelector("#planBarChart"), {
        chart: { type: "bar", height: 280, toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 6, columnWidth: "45%" } },
        series: [{ name: "Subscribers", data: planCounts }],
        colors: ["#3b82f6"],
        xaxis: { categories: planNames },
        grid: { borderColor: "#f3f4f6" },
      }).render();
    },
  },
};
</script>

<style scoped>
.card {
  @apply bg-white border border-gray-100 rounded-2xl shadow-sm p-6 transition hover:shadow-md;
}
.chart-wrapper {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: block;
  box-sizing: border-box;
}
</style>
